/**
 * The org's ONE activity socket — realtime protocol v2 (TASK-7922504E, batch
 * contract C-12).
 *
 * One WebSocket per signed-in tab and org, on `/api/v1/ws/:orgId?token=`,
 * carrying MANY contextIds (`org:<id>`, `user:<id>`, `project:<slugOrHex>`,
 * `task:<legacyId>`). Components hold contextIds through {@link ActivitySocket.retain};
 * the server authorises each one at subscribe time and answers with an ack
 * that grants or refuses it.
 *
 * - **Ref-counted holds, coalesced per microtask.** Only a net change reaches
 *   the wire: 0→1 sends one `subscribe`, 1→0 one `unsubscribe`, and a
 *   retain→release→retain inside one tick (React StrictMode's
 *   mount→cleanup→mount) sends nothing extra. The same coalescing applies to
 *   {@link ActivitySocket.start} and {@link ActivitySocket.setToken}, so a
 *   StrictMode double effect opens one connection, not two.
 * - **Every (re)open re-subscribes the whole held set** in one frame (chunked at
 *   {@link MAX_CONTEXT_IDS_PER_MESSAGE}); a token change reconnects with the
 *   new token and re-subscribes the same set. While (re)connecting every held
 *   contextId reads `pending`.
 * - **Each event frame is handed to the frame sinks exactly once** (the provider
 *   registers `dispatchActivity`), however many holds it matched; per-context
 *   handlers receive it when their ids meet the frame's `subscriptions`.
 *   Control frames (`control: "ack" | "error"`) never reach a sink.
 * - Status snapshots are canonical immutable objects replaced only on change,
 *   so `useSyncExternalStore` readers get stable references.
 *
 * Reconnect: exponential backoff 1 s → 30 s. Liveness: `PING` every 10 s, the
 * server answers `PONG`. The JWT rides `?token=` because browser WebSocket
 * clients cannot set headers; neither the token nor the URL is ever logged.
 */

import { getApiBaseUrl } from '@/services/apiBaseUrl'
import type { ActivityEvent, ActivityHandler, ConnectionStatus } from './activityClient'

/** Server-side authorisation state of one held contextId. */
export type ContextStatus =
  | { readonly state: 'pending' }
  | { readonly state: 'granted' }
  | { readonly state: 'refused'; readonly reason: string }

/** The one `pending` status object (stable reference). */
export const PENDING_STATUS: ContextStatus = Object.freeze({ state: 'pending' })
/** The one `granted` status object (stable reference). */
export const GRANTED_STATUS: ContextStatus = Object.freeze({ state: 'granted' })

const refusedByReason = new Map<string, ContextStatus>()

/** The canonical `refused` status for a reason (one object per reason). */
export function refusedStatus(reason: string): ContextStatus {
  let status = refusedByReason.get(reason)
  if (status === undefined) {
    status = Object.freeze({ state: 'refused', reason })
    refusedByReason.set(reason, status)
  }
  return status
}

/** The server's per-message cap (C-12); larger holds are split across frames. */
export const MAX_CONTEXT_IDS_PER_MESSAGE = 32

const PING_INTERVAL_MS = 10_000
const BASE_RECONNECT_DELAY_MS = 1_000
const MAX_RECONNECT_DELAY_MS = 30_000
/** `WebSocket.OPEN` — spelled out so a test double need not carry the static. */
const SOCKET_OPEN = 1

export interface ActivitySocketOptions {
  /** The org the socket is bound to for its lifetime. */
  orgId: string
  /** Overrides the `ws(s)://host/api/v1` base derived from the API base URL. */
  wsBaseUrl?: string
}

export interface ActivitySocket {
  readonly orgId: string
  /**
   * Declare that the socket should be live (ref-counted). Returns the matching
   * stop; the connection closes once every start has been stopped.
   */
  start(): () => void
  /** The access token to connect with; a change reconnects, `null` disconnects. */
  setToken(token: string | null): void
  /** Hold contextIds for as long as the returned release has not been called. */
  retain(contextIds: readonly string[]): () => void
  /** Authorisation status of a contextId (`pending` when not held). */
  statusOf(contextId: string): ContextStatus
  /** Notified whenever any contextId's status changes. */
  subscribeStatus(listener: () => void): () => void
  /** Receive every event frame exactly once. */
  onFrame(sink: ActivityHandler): () => void
  /** Receive event frames whose `subscriptions` meet `contextIds` (once per frame). */
  onContextEvent(contextIds: readonly string[], handler: ActivityHandler): () => void
  getConnectionStatus(): ConnectionStatus
  onConnectionStatus(listener: () => void): () => void
}

interface ContextHandlerEntry {
  ids: ReadonlySet<string>
  handler: ActivityHandler
}

interface SubscribeAck {
  control: 'ack'
  op: 'subscribe'
  granted?: unknown
  refused?: unknown
}

/** Resolve the WebSocket base URL from the API base URL (absolute or relative). */
function defaultWsBaseUrl(): string {
  const apiBase = getApiBaseUrl()
  if (/^https?:/i.test(apiBase)) {
    return apiBase.replace(/^https:/i, 'wss:').replace(/^http:/i, 'ws:')
  }
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const path = apiBase.startsWith('/') ? apiBase : '/' + apiBase
  return `${scheme}//${window.location.host}${path}`
}

function notifyAll(listeners: Set<() => void>): void {
  for (const listener of listeners) {
    try {
      listener()
    } catch (err) {
      console.error('[ActivitySocket] listener error:', err)
    }
  }
}

function isSubscribeAck(frame: Record<string, unknown>): frame is Record<string, unknown> & SubscribeAck {
  return frame.control === 'ack' && frame.op === 'subscribe'
}

/** Create the (initially idle) socket for one org; nothing connects until started with a token. */
export function createActivitySocket({ orgId, wsBaseUrl }: ActivitySocketOptions): ActivitySocket {
  const refCounts = new Map<string, number>()
  const statuses = new Map<string, ContextStatus>()
  /** The ids the CURRENT connection has been sent a subscribe for (granted or refused). */
  const serverHeld = new Set<string>()
  const statusListeners = new Set<() => void>()
  const connectionListeners = new Set<() => void>()
  const sinks = new Set<ActivityHandler>()
  const contextHandlers = new Set<ContextHandlerEntry>()

  let token: string | null = null
  let starts = 0
  let connectionStatus: ConnectionStatus = 'disconnected'
  let ws: WebSocket | null = null
  /** The token the live (or reconnecting) connection was opened with. */
  let wsToken: string | null = null
  let pingTimer: ReturnType<typeof setInterval> | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let reconnectAttempts = 0
  let refSeq = 0
  let flushScheduled = false

  const wanted = (): boolean => starts > 0 && token !== null

  function setConnectionStatus(next: ConnectionStatus): void {
    if (next === connectionStatus) return
    connectionStatus = next
    notifyAll(connectionListeners)
  }

  function setStatus(contextId: string, next: ContextStatus): boolean {
    if (statuses.get(contextId) === next) return false
    statuses.set(contextId, next)
    return true
  }

  function markAllPending(): void {
    let changed = false
    for (const contextId of statuses.keys()) {
      changed = setStatus(contextId, PENDING_STATUS) || changed
    }
    if (changed) notifyAll(statusListeners)
  }

  function clearPing(): void {
    if (pingTimer !== null) {
      clearInterval(pingTimer)
      pingTimer = null
    }
  }

  function clearReconnect(): void {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  function send(frame: string): void {
    if (ws !== null && ws.readyState === SOCKET_OPEN) ws.send(frame)
  }

  function sendChunked(op: 'subscribe' | 'unsubscribe', contextIds: string[]): void {
    for (let index = 0; index < contextIds.length; index += MAX_CONTEXT_IDS_PER_MESSAGE) {
      refSeq += 1
      send(JSON.stringify({ op, ref: `c${refSeq}`, contextIds: contextIds.slice(index, index + MAX_CONTEXT_IDS_PER_MESSAGE) }))
    }
  }

  /** Bring the server's view of the held set in line with the ref counts (net changes only). */
  function syncSubscriptions(): void {
    if (ws === null || ws.readyState !== SOCKET_OPEN) return
    const toSubscribe = [...refCounts.keys()].filter((contextId) => !serverHeld.has(contextId))
    const toUnsubscribe = [...serverHeld].filter((contextId) => !refCounts.has(contextId))
    for (const contextId of toSubscribe) serverHeld.add(contextId)
    for (const contextId of toUnsubscribe) serverHeld.delete(contextId)
    sendChunked('unsubscribe', toUnsubscribe)
    sendChunked('subscribe', toSubscribe)
  }

  /** Forget the status of every id nobody holds any more. */
  function dropReleasedStatuses(): void {
    let changed = false
    for (const contextId of [...statuses.keys()]) {
      if (!refCounts.has(contextId)) {
        statuses.delete(contextId)
        changed = true
      }
    }
    if (changed) notifyAll(statusListeners)
  }

  function teardown(): void {
    clearPing()
    clearReconnect()
    if (ws !== null) {
      const closing = ws
      ws = null
      closing.onopen = null
      closing.onmessage = null
      closing.onclose = null
      closing.onerror = null
      closing.close()
    }
    wsToken = null
    serverHeld.clear()
    markAllPending()
  }

  function onSubscribeAck(frame: SubscribeAck): void {
    let changed = false
    const granted = Array.isArray(frame.granted) ? frame.granted : []
    for (const contextId of granted) {
      if (typeof contextId === 'string' && refCounts.has(contextId)) {
        changed = setStatus(contextId, GRANTED_STATUS) || changed
      }
    }
    const refused = Array.isArray(frame.refused) ? frame.refused : []
    for (const entry of refused) {
      if (entry === null || typeof entry !== 'object') continue
      const { contextId, reason } = entry as { contextId?: unknown; reason?: unknown }
      if (typeof contextId === 'string' && typeof reason === 'string' && refCounts.has(contextId)) {
        changed = setStatus(contextId, refusedStatus(reason)) || changed
      }
    }
    if (changed) notifyAll(statusListeners)
  }

  function onEventFrame(event: ActivityEvent): void {
    for (const sink of sinks) {
      try {
        sink(event)
      } catch (err) {
        console.error(`[ActivitySocket] frame sink error for ${event.type}:`, err)
      }
    }
    const { subscriptions = [] } = event
    if (subscriptions.length === 0 || contextHandlers.size === 0) return
    for (const entry of [...contextHandlers]) {
      if (!subscriptions.some((contextId) => entry.ids.has(contextId))) continue
      try {
        entry.handler(event)
      } catch (err) {
        console.error(`[ActivitySocket] context handler error for ${event.type}:`, err)
      }
    }
  }

  function onMessage(raw: string): void {
    if (raw === 'PONG') return
    let frame: unknown
    try {
      frame = JSON.parse(raw)
    } catch {
      return
    }
    if (frame === null || typeof frame !== 'object' || Array.isArray(frame)) return
    const record = frame as Record<string, unknown>
    if (typeof record.control === 'string') {
      if (isSubscribeAck(record)) {
        onSubscribeAck(record)
      } else if (record.control === 'error') {
        console.warn('[ActivitySocket] the server rejected a frame:', record.reason)
      }
      return
    }
    if (typeof record.type !== 'string') return
    onEventFrame(record as unknown as ActivityEvent)
  }

  function scheduleReconnect(): void {
    clearReconnect()
    if (!wanted()) return
    const delay = Math.min(BASE_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempts), MAX_RECONNECT_DELAY_MS)
    reconnectAttempts += 1
    setConnectionStatus('reconnecting')
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      open()
    }, delay)
  }

  function open(): void {
    if (!wanted() || token === null) return
    wsToken = token
    const url = `${wsBaseUrl ?? defaultWsBaseUrl()}/ws/${encodeURIComponent(orgId)}?token=${encodeURIComponent(token)}`
    setConnectionStatus(reconnectAttempts === 0 ? 'connecting' : 'reconnecting')
    let socket: WebSocket
    try {
      socket = new WebSocket(url)
    } catch {
      // The error would carry the URL, and with it the token: never logged.
      console.error('[ActivitySocket] could not open the activity socket')
      scheduleReconnect()
      return
    }
    ws = socket
    socket.onopen = () => {
      if (ws !== socket) return
      reconnectAttempts = 0
      setConnectionStatus('connected')
      clearPing()
      pingTimer = setInterval(() => send('PING'), PING_INTERVAL_MS)
      serverHeld.clear()
      syncSubscriptions()
    }
    socket.onmessage = (event: MessageEvent) => {
      if (ws !== socket) return
      onMessage(String(event.data))
    }
    socket.onclose = () => {
      if (ws !== socket) return
      ws = null
      clearPing()
      serverHeld.clear()
      markAllPending()
      setConnectionStatus('disconnected')
      scheduleReconnect()
    }
    socket.onerror = () => {
      // onclose follows onerror; the reconnect path lives there.
      if (ws === socket) socket.close()
    }
  }

  function reconcileConnection(): void {
    if (!wanted()) {
      if (ws !== null || reconnectTimer !== null || wsToken !== null) teardown()
      reconnectAttempts = 0
      setConnectionStatus('disconnected')
      return
    }
    if (wsToken !== token) {
      teardown()
      reconnectAttempts = 0
      open()
    }
  }

  function flush(): void {
    flushScheduled = false
    reconcileConnection()
    syncSubscriptions()
    dropReleasedStatuses()
  }

  function scheduleFlush(): void {
    if (flushScheduled) return
    flushScheduled = true
    queueMicrotask(flush)
  }

  return {
    orgId,
    start() {
      starts += 1
      scheduleFlush()
      let stopped = false
      return () => {
        if (stopped) return
        stopped = true
        starts -= 1
        scheduleFlush()
      }
    },
    setToken(next) {
      if (next === token) return
      token = next
      scheduleFlush()
    },
    retain(contextIds) {
      const ids = [...new Set(contextIds)]
      let changed = false
      for (const contextId of ids) {
        const count = (refCounts.get(contextId) ?? 0) + 1
        refCounts.set(contextId, count)
        if (count === 1 && !statuses.has(contextId)) {
          statuses.set(contextId, PENDING_STATUS)
          changed = true
        }
      }
      if (changed) notifyAll(statusListeners)
      scheduleFlush()
      let released = false
      return () => {
        if (released) return
        released = true
        for (const contextId of ids) {
          const count = (refCounts.get(contextId) ?? 0) - 1
          if (count > 0) refCounts.set(contextId, count)
          else refCounts.delete(contextId)
        }
        scheduleFlush()
      }
    },
    statusOf(contextId) {
      return statuses.get(contextId) ?? PENDING_STATUS
    },
    subscribeStatus(listener) {
      statusListeners.add(listener)
      return () => {
        statusListeners.delete(listener)
      }
    },
    onFrame(sink) {
      sinks.add(sink)
      return () => {
        sinks.delete(sink)
      }
    },
    onContextEvent(contextIds, handler) {
      const entry: ContextHandlerEntry = { ids: new Set(contextIds), handler }
      contextHandlers.add(entry)
      return () => {
        contextHandlers.delete(entry)
      }
    },
    getConnectionStatus() {
      return connectionStatus
    },
    onConnectionStatus(listener) {
      connectionListeners.add(listener)
      return () => {
        connectionListeners.delete(listener)
      }
    }
  }
}
